import { AppFeaturesSystemName, QuerySelectExpr } from '@apis/Customers/model';
import { NotificationComponentConfig, NotificationComponentConfigType } from '@apis/Notification/model';
import { QueryExpr, QueryField } from '@apis/Resources';
import { Query, ObjectQueryResult, SchemaType, SchemaField } from '@apis/Resources/model';
import { UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { DetailedLineChartSettings } from '@root/Components/Charts/DetailedLineChart';
import { GridChartSettings } from '@root/Components/Charts/GridChart';
import { KpiChartSettings } from '@root/Components/Charts/KpiChart';
import { PieChartSettings, UserConfiguredPieChartSettings } from '@root/Components/Charts/PieChart';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { createDatasourceSchemaContext, DatasourceSchemaContext } from '@root/Components/Filter/DatasourceContext';
import { QueryDescriptorService } from '@root/Components/Filter/Services';
import { AppFeatureNames } from '@root/Services/Customers/CompanyFeatureService';
import { NamedFormats } from '@root/Services/FormatService';
import { IValueProviderFactory, QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import {
    ISupplementalFieldInfo,
    PrimitiveType,
    queryBuilder,
    SchemaService,
    SchemaValueProvider,
    sortedObjectId,
    ValuesGroupOtherText,
} from '@root/Services/QueryExpr';
import { BorderAll, ChartBar, ChartLine, ChartPie, CircleNumber1, Icon, QuestionMark } from 'tabler-icons-react';

type TypeDetail = {
    type: NotificationComponentConfigType | 'Unspecified';
    subType?: string;
    description: string;
    dimensionComponent?: {
        name: string;
        plural: string;
        asc: string;
        desc: string;
    };
    icon: Icon;
};
const typeDetails: TypeDetail[] = [
    {
        type: 'Chart',
        subType: 'pie',
        description: 'Pie Chart',
        dimensionComponent: {
            name: 'slice',
            plural: 'slices',
            asc: 'clockwise from the top',
            desc: 'counter-clockwise from the top',
        },
        icon: ChartPie,
    },
    {
        type: 'Chart',
        subType: 'grid',
        description: 'Table',
        dimensionComponent: {
            name: 'row',
            plural: 'rows',
            asc: 'from the top, going down',
            desc: 'from the bottom, going up',
        },
        icon: BorderAll,
    },
    {
        type: 'Chart',
        subType: 'bar',
        description: 'Bar Chart',
        dimensionComponent: {
            name: 'bar',
            plural: 'bars',
            asc: 'from left-most to right',
            desc: 'from right-most to left',
        },
        icon: ChartBar,
    },
    {
        type: 'Chart',
        subType: 'line',
        description: 'Line Chart',
        dimensionComponent: {
            name: 'point',
            plural: 'points',
            asc: 'from oldest to newest',
            desc: 'from newest to oldest',
        },
        icon: ChartLine,
    },
    {
        type: 'Chart',
        subType: 'kpi',
        description: 'KPI',
        icon: CircleNumber1,
    },
    {
        type: 'DataGrid',
        description: 'Table',
        icon: BorderAll,
    },
];

const typeLookup = {
    types: new Map<string, TypeDetail>(typeDetails.map((td) => [`${td.type}:${td.subType}`, td])),
    get: (type: string, subType?: string) => typeLookup.types.get(`${type}:${subType}`),
};

export function getComponentTypeDescriptors() {
    return typeDetails;
}
export function describeComponent(componentConfig: NotificationComponentConfig): TypeDetail {
    const type = componentConfig.Type;
    const subType = type === 'Chart' ? (componentConfig as { chartType: string }).chartType : undefined;
    const baseDescriptor = describeComponentType(type, subType);

    const dimensionComponent = baseDescriptor.dimensionComponent ? { ...baseDescriptor.dimensionComponent } : undefined;
    if (dimensionComponent) {
        switch (subType) {
            case 'bar':
                const barSettings = (componentConfig as { settings: UserConfiguredBarChartSettings }).settings;
                if (barSettings.orientation === 'Horizontal') {
                    Object.assign(dimensionComponent, { asc: 'from the bottom, going up', desc: 'grom the top, going down' });
                }
                break;
        }
    }

    return { ...baseDescriptor, dimensionComponent };
}
export function describeComponentType(componentType?: null | string, componentSubType?: null | string): TypeDetail {
    const subType = componentSubType ?? undefined;
    const baseDescriptor = {
        ...(typeLookup.get(componentType ?? '', subType) ?? {
            type: 'Unspecified',
            description: 'Not Configured',
            icon: QuestionMark,
        }),
    };
    return baseDescriptor;
}

type ComponentPostSort = { sortBy: 'value' | 'label'; sortDir: 'asc' | 'desc' };
type ComponentAttributeSetConfig = { datasetId: string; sort?: ComponentPostSort; attrs: Array<IComponentAttribute>; filterId?: string };
class ComponentAttributeSet {
    private _schemaCtx: DatasourceSchemaContext | undefined;
    private readonly attrByAlias = new Map<string, IComponentAttribute>();

    public readonly metrics: ReadonlyArray<IComponentAttribute> = [];
    public readonly dimensions: ReadonlyArray<IComponentAttribute> = [];
    public readonly attrs: ReadonlyArray<IComponentAttribute>;
    public readonly postSort: undefined | { sortBy: 'value' | 'label'; sortDir: 'asc' | 'desc' };
    public readonly datasetId: string;
    public readonly filterId: string | undefined;

    public get hasMetrics() {
        return this.metrics.length > 0;
    }
    public get hasDimensions() {
        return this.dimensions.length > 0;
    }
    public get schemaCtx() {
        return (this._schemaCtx ??= this.createDatasource(this));
    }

    public constructor(
        config: ComponentAttributeSetConfig,
        public readonly descriptor: Readonly<TypeDetail>,
        private readonly baseSchemaCtx: DatasourceSchemaContext
    ) {
        const { datasetId, sort, attrs, filterId } = config;
        this.postSort = sort;
        this.attrs = attrs.filter((a) => !!a) as IComponentAttribute[];
        this.datasetId = datasetId;
        this.filterId = filterId;
        this.metrics = attrs.filter((a) => a.fieldType === 'metric');
        this.dimensions = attrs.filter((a) => a.fieldType === 'dim');
        this.attrByAlias = attrs.reduce((r, attr) => r.set(attr.alias, attr), new Map<string, IComponentAttribute>());
    }

    public getByAlias(alias: string): IComponentAttribute | undefined {
        return this.attrByAlias.get(alias);
    }

    public subset(filter: (attr: IComponentAttribute) => boolean): IComponentAttributeSet {
        const attrs = this.attrs.filter(filter);
        const config = { datasetId: this.datasetId, sort: this.postSort, attrs };
        return new ComponentAttributeSet(config, this.descriptor, this.baseSchemaCtx);
    }

    private createDatasource(attrs: IComponentAttributeSet): DatasourceSchemaContext {
        return new ComponentQueryDatasourceService().createDatasource(attrs, this.baseSchemaCtx);
    }
}
export interface IComponentAttributeSet extends ComponentAttributeSet {}

export interface IComponentAttribute {
    alias: string;
    expr: QueryExpr;
    exprType: PrimitiveType;
    fieldType: 'dim' | 'metric';
    format?: NamedFormats;
    id: string;
    index: number;
    name: string;
    selectExpr: QuerySelectExpr;
}
export class ComponentAttributeService {
    public constructor(private readonly schemaCtx: DatasourceSchemaContext) {}

    public createAttributes(config: NotificationComponentConfig): IComponentAttributeSet {
        const descriptor = describeComponent(config);
        if (descriptor.type !== 'Chart' || !descriptor.subType) {
            return new ComponentAttributeSet({ attrs: [], datasetId: '' }, descriptor, this.schemaCtx);
        } else {
            const attrConfig = this.createChartAttrs(config as unknown as ChartConfig);
            return new ComponentAttributeSet(attrConfig, descriptor, this.schemaCtx);
        }
    }

    private createChartAttrs(cfg: ChartConfig) {
        const type = cfg.chartType.toLowerCase();
        const { groups, values } = cfg;
        switch (type) {
            case 'pie':
                return this.withTypedSettings<UserConfiguredPieChartSettings>(cfg, (s) => ({
                    datasetId: this.getSingleDimMetricDatasetId(cfg),
                    filterId: JSON.stringify(cfg.filters),
                    sort: { sortBy: s.sortOptions?.sortBy ?? 'value', sortDir: s.sortOptions?.sortDir ?? 'desc' },
                    attrs: [
                        this.createAttr(0, 'dim', groups[0]),
                        this.createAttr(0, 'metric', values[0], typeof s.valueFormat === 'function' ? undefined : s.valueFormat, undefined, 'number'),
                    ],
                }));
            case 'bar':
                return this.withTypedSettings<UserConfiguredBarChartSettings>(cfg, (s) => ({
                    datasetId: this.getSingleDimMetricDatasetId(cfg),
                    filterId: JSON.stringify(cfg.filters),
                    sort: { sortBy: s.reaggOptions?.sortBy ?? 'value', sortDir: s.reaggOptions?.sortDir ?? 'desc' },
                    attrs: [
                        this.createAttr(0, 'dim', groups[0], s.xFormat),
                        this.createAttr(1, 'dim', groups[1]),
                        this.createAttr(0, 'metric', values[0], s.format, s.metricLabel, 'number'),
                    ],
                }));
            case 'line':
                return this.withTypedSettings<DetailedLineChartSettings>(cfg, (s) => ({
                    datasetId: this.getSingleDimMetricDatasetId(cfg),
                    filterId: JSON.stringify(cfg.filters),
                    sort: { sortBy: 'label', sortDir: 'asc' },
                    attrs: [
                        this.createAttr(0, 'dim', groups[0], s.xFormat, undefined, 'date'),
                        this.createAttr(1, 'dim', groups[1]),
                        this.createAttr(0, 'metric', values[0], s.format, s.labels?.y, 'number'),
                    ],
                }));
            case 'kpi':
                return this.withTypedSettings<KpiChartSettings>(cfg, (s) => ({
                    filterId: JSON.stringify(cfg.filters),
                    datasetId: this.getDatasetId(...cfg.values),
                    attrs: cfg.values.map((v, i) => this.createAttr(i, 'metric', v, s.format[i], s.labels[i] || `KPI ${i + 1}`, 'number')),
                }));

            case 'grid':
                return this.withTypedSettings<GridChartSettings>(cfg, (s) => ({
                    filterId: JSON.stringify(cfg.filters),
                    datasetId: sortedObjectId(s.columns.map((c) => [c.select, c.missingValue])),
                    attrs: this.createGridAttrs(s),
                }));
            default:
                return {
                    filterId: JSON.stringify(cfg.filters),
                    datasetId: this.getDatasetId(...groups, ...values),
                    attrs: [...groups.map((g, i) => this.createAttr(i, 'dim', g)), ...values.map((v, i) => this.createAttr(i, 'metric', v))],
                };
        }
    }

    //#region Component type specific attribute creation
    private createGridAttrs(s: GridChartSettings) {
        type ColType = GridChartSettings['columns'][number];
        const getFieldType = (col?: ColType) => {
            const opInfo = this.schemaCtx.queryDescriptorSvc.getOperationInfo(col?.select?.Expr ?? '');
            return opInfo?.flags?.includes('agg') && opInfo.type === 'number' ? 'metric' : ('dim' as const);
        };
        return s.columns.reduce((result, col) => {
            const fmt = typeof col?.formatter === 'string' ? col.formatter : undefined;
            const name = col?.select.Alias || undefined;
            const attr = this.createAttr(result.length, getFieldType(col), col?.select, fmt, name, col?.type);
            if (attr) {
                attr.alias = col.id;
                result.push(attr);
            }
            return result;
        }, [] as IComponentAttribute[]);
    }
    //#endregion

    private getSingleDimMetricDatasetId(cfg: ChartConfig) {
        const { groups, values } = cfg;
        return this.getDatasetId(groups[0]?.Expr, values[0]?.Expr);
    }
    private getDatasetId(...exprs: Array<undefined | QuerySelectExpr>) {
        return sortedObjectId(exprs.map((x) => x?.Expr));
    }

    private withTypedSettings<T>(cfg: ChartConfig, callback: (settings: T) => ComponentAttributeSetConfig) {
        const { attrs, datasetId, filterId, sort } = callback(this.typedSettings<T>(cfg));
        return {
            attrs: attrs.filter((t) => !!t),
            datasetId,
            filterId,
            sort,
        };
    }
    private typedSettings<T>(cfg: ChartConfig) {
        return cfg.settings as T;
    }

    private createAttr(
        index: number,
        fieldType: 'dim' | 'metric',
        selectExpr: QuerySelectExpr | undefined,
        format?: NamedFormats | string,
        name?: string,
        exprType?: string
    ): IComponentAttribute {
        if (!selectExpr) {
            return undefined!;
        }
        const queryDescSvc = this.schemaCtx.queryDescriptorSvc;

        const expr = selectExpr.Expr as QueryExpr;
        const id = `${fieldType}-${index}-${fieldType === 'metric' ? exprType : queryDescSvc.getName(expr)}`;

        return {
            alias: selectExpr.Alias!,
            expr,
            exprType: (exprType ?? queryDescSvc.getExprType(expr)) as PrimitiveType,
            fieldType,
            format: (format ?? queryDescSvc.getFormatter(expr)?.id) as NamedFormats | undefined,
            id,
            index,
            name: name || queryDescSvc.getName(expr) || selectExpr.Alias!,
            selectExpr,
        };
    }
}

class ComponentQueryDatasourceService {
    public createDatasource(attrs: IComponentAttributeSet, baseSchemaCtx: DatasourceSchemaContext): DatasourceSchemaContext {
        const chartSchema = this.createChartSchema(attrs);
        const schemaSvc = SchemaService.create(chartSchema);
        const chartValueProvider = this.createChartValueProvider(attrs, baseSchemaCtx);
        const datasource = this.createChartDs(attrs, chartSchema, baseSchemaCtx, chartValueProvider);
        const queryDescriptorSvc = QueryDescriptorService.create(schemaSvc);
        const valueProvider = SchemaValueProvider.createWithOverride(schemaSvc, chartValueProvider.getValueProvider);

        return { schemaSvc, queryDescriptorSvc, valueProvider, datasource };
    }

    private createChartDs(
        attrs: IComponentAttributeSet,
        chartSchema: SchemaType[],
        baseSchemaCtx: DatasourceSchemaContext,
        valueProvider: IValueProviderFactory
    ): QueryDatasource {
        const { datasource: ds } = baseSchemaCtx;
        const chartType = attrs.descriptor.subType ?? 'unknownchart';
        const { source } = this.createEmptyDs();
        const groups = attrs.subset((a) => a.fieldType === 'dim' && a.exprType === 'string').attrs;
        const histograms = attrs.subset((a) => a.fieldType === 'metric' && a.exprType === 'number').attrs;
        const values = attrs.subset((a) => a.fieldType === 'metric' && a.exprType === 'number').attrs;
        const result: QueryDatasource = {
            name: ds.name + '-' + chartType,
            feature: AppFeatureNames.None,
            source,
            getDefaultGroup: () => groups[0]?.selectExpr ?? {},
            getDefaultHistogram: () => histograms[0]?.selectExpr ?? {},
            getDefaultValue: () => values[0]?.selectExpr ?? {},
            getDefaultFilterFields: () => (groups.length ? [groups[0].alias] : []),
            getValueProviderFactory: () => valueProvider,
            noun: '',
            nounPlural: '',
            title: `${attrs.descriptor.description} Data`,
            schema: { getSchema: () => Promise.resolve(chartSchema) },
        };
        return result;
    }

    private createChartSchema(attrs: IComponentAttributeSet): SchemaType[] {
        type FieldType = SchemaField & ISupplementalFieldInfo;
        return [
            {
                IsRoot: true,
                Name: 'Fields',
                TypeId: 'component-fields',
                Fields: [
                    ...attrs.dimensions.map(
                        (d): FieldType => ({
                            Name: d.name,
                            TypeName: d.exprType,
                            Field: d.alias,
                            IsPrimitive: true,
                            Format: d.format,
                            Expr: d.expr,
                        })
                    ),
                    ...attrs.metrics.map(
                        (m): FieldType => ({
                            Name: m.name,
                            Field: m.alias,
                            TypeName: m.exprType,
                            IsPrimitive: true,
                            Format: m.format,
                        })
                    ),
                ],
            },
        ];
    }

    private createChartValueProvider(attrs: IComponentAttributeSet, baseSchemaCtx: DatasourceSchemaContext) {
        const comparer = Intl.Collator(undefined, { sensitivity: 'base' }).compare;
        const getValueProvider = (field: QueryField) => {
            const attr = attrs.getByAlias(field.Field);
            const fieldExpr = attr?.exprType !== 'string' || attr?.fieldType !== 'dim' ? undefined : attr.expr;
            return !fieldExpr
                ? undefined
                : async (filter: string) => {
                      const results = await queryBuilder<Record<string, string>>()
                          .select((b) => ({
                              value: b.values(b.fromExpr<string>(fieldExpr), filter, ValuesGroupOtherText),
                              ct: b.count(),
                          }))
                          .execute(baseSchemaCtx.datasource.source);
                      return (results?.Results ?? [])
                          .map((r) => r.value)
                          .filter((v) => v !== ValuesGroupOtherText)
                          .sort(comparer);
                  };
        };
        return { getValueProvider };
    }

    private createEmptyDs(): QueryDatasource {
        return {
            getDefaultGroup: () => ({}),
            getDefaultHistogram: () => ({}),
            getDefaultValue: () => ({}),
            getDefaultFilterFields: () => [],
            name: 'none',
            feature: AppFeatureNames.None,
            schema: { getSchema: () => Promise.resolve<SchemaType[]>([]) },
            source: () => Promise.resolve<ObjectQueryResult>({}),
        };
    }
}
